<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-stepper v-if="!id" :steps="rmSteps" :current="currentStep" />
        <hm-spinner v-if="!ready" />
        <Loading :active="busy" :is-full-page="false">
          <slot>
            <hm-spinner />
          </slot>
        </Loading>
        <hm-alert />

        <b-form @submit.prevent="onSubmit()" v-show="ready">
          <h4 v-if="!id">Step 6: Review &amp; Approve Risk Management Report</h4>
          <h3 v-else>Risk Management Report</h3>
          <hr />
          <p>
            This report documents the development and implementation of a risk management plan, in accordance with the implementation specification at § 164.308(a)(1)(ii)(B), was conducted by
            <span
              class="ra-highlight"
            >{{rm.seed.practice.po.name}}</span> (&quot;Privacy Officer&quot;) on
            <span class="ra-highlight">{{rm.completedDate | formatDate('MMMM DD, YYYY')}}</span> for
            <span class="ra-highlight">{{rm.seed.practice.name}}</span> located at
            <span class="ra-highlight">
              {{rm.seed.practice.address1}}, {{rm.seed.practice.address2}}
              <span v-if="rm.seed.practice.address2">,</span>
              {{rm.seed.practice.city}}, {{rm.seed.practice.state}} {{rm.seed.practice.zip}}
            </span> (&quot;Practice&quot;).
          </p>

          <h4 class="pt-2">1. Purpose</h4>
          <p>The purpose of a risk management plan is to provide structure for the Practice’s evaluation, prioritization, and implementation of risk-reducing security measures.</p>

          <h4 class="pt-2">2. Policy</h4>
          <p>For the risk management plan to be successful, key members of the Practice's workforce, including senior management and other key decision makers, must be involved. The outputs of the risk analysis process provides these key workforce members with the information needed to make risk prioritization and mitigation decisions.</p>
          <p>The risk prioritization and mitigation decisions will be determined by answering questions such as&colon;</p>
          <ul>
            <li>Should certain risks be addressed immediately or in the future?</li>
            <li>Which security measures should be implemented?</li>
          </ul>

          <p>Many of the answers to these questions will be determined using data gathered during the risk analysis. The Practice has already identified, through that process, what vulnerabilities exist, and what the impact of the risk could be to the organization. This data will allow the covered entity to make informed decisions on how to reduce risks to reasonable and appropriate levels.</p>
          <p>An important component of the risk management plan is the plan for implementation of the selected security measures. The implementation component of the plan should address&colon;</p>
          <ul>
            <li>Risks (threat and vulnerability combinations) being addressed&semi;</li>
            <li>Security measures selected to reduce the risks&semi;</li>
            <li>Implementation project priorities, such as&colon; cost&semi; assigned responsibilities&semi; and due dates.</li>
          </ul>

          <p>Compliance with the Security Rule requires financial resources, management commitment, and workforce involvement. Cost is one of the factors the Practice considered when determining security measures to implement. However, cost was not the sole determinant when choosing not to implement security measures that are reasonable and appropriate.</p>
          <p>The output of this step is a risk management plan that contains prioritized risks, options for mitigation of those risks, and a plan for implementation. The plan will guide the Practice's actual implementation of security measures to reduce risks to ePHI to reasonable and appropriate levels.</p>

          <h4 class="pt-2">3. Procedures</h4>
          <p>Create a risk management plan (&quot;task&quot;) for each threat-vulnerability combination identified through the risk analysis process. Begin with items having the highest risk level and work towards items with the lowest risk level.</p>

          <h6>Task Generation</h6>
          <p>For each threat-vulnerability combination identified by risk analysis&colon;</p>
          <ol type="1">
            <li>Review risk level and general action description (timeline) - to help guide senior management decision making.</li>
            <li>Review safeguards currently implemented to protect against the threat.</li>
            <li>Decide what to do about additional HIPAA safeguards not implemented. If a safeguard is required by HIPAA it must be implemented. Otherwise, either implement the safeguard or, alternatively, you may choose not to implement the safeguard as long as you indicate why not. Reasons why not include&colon;</li>
            <ol type="a">
              <li v-for="(value, key) in riskMap.SafeguardReason" :key="key">{{value.title}}</li>
            </ol>
            <li>Specify to what, where, or whom the safeguard is to be applied.</li>
            <li>Provide estimated cost for implementation.</li>
            <li>Identify workforce member or business associate (BA) responsible for implementation.</li>
            <li>Schedule a due date.</li>
          </ol>

          <h6>Work Tasks</h6>
          <p>All tasks are worked by assigned workforce or BA until completed. The Privacy Office shall manage the overall process, and may edit tasks or remove from time to time depending on practice circumstances.</p>

          <h6>Documentation</h6>
          <p>To maintain visibility and accountability into the Practice's risk management effort, the Practice shall keep a record of approved Risk Management Plans and completed risk management tasks.</p>

          <h4 class="pt-4">4. Risk Management Plan Tasks</h4>
          <hr />
          <div v-if="safeguardsNeg.length == 0">
            <h6>None to report.</h6>
            <hr />
          </div>
          <div v-for="safeguard in safeguardsNeg" :key="safeguard.id">
            <h4>Safeguard: {{safeguard.title}}</h4>
            <p class="prewrap">{{safeguard.desc}}</p>

            <h6>Threat-Vulnerability Combinations Affected</h6>
            <div v-for="threat in threatsForSafeguard(safeguard.id)" :key="threat.id">
              <i class="fa fa-caret-right pr-2"></i>
              {{threat.threat}} - {{threat.vuln}}:
              <span class="text-danger" v-if="threat.risk >= 20">
                <b-badge variant="danger" class="mx-2">{{threat.risk}}</b-badge>(Immediate implementation of corrective measures)
              </span>
              <span class="text-warning" v-if="threat.risk >= 12 && threat.risk <= 19">
                <b-badge variant="warning" class="mx-2">{{threat.risk}}</b-badge>(Near future implementation of corrective measures)
              </span>
              <span class="text-primary" v-if="threat.risk >= 6 && threat.risk <= 11">
                <b-badge variant="primary" class="mx-2">{{threat.risk}}</b-badge>(Earliest convenience implementation of corrective measures)
              </span>
              <span class="text-success" v-if="threat.risk <= 5">
                <b-badge variant="success" class="mx-2">{{threat.risk}}</b-badge>(Least urgent implementation of corrective measures)
              </span>
            </div>

            <h6 class="pt-3">Plan</h6>
            <div v-if="rm.safeguards[safeguard.id].implement">
              <div v-if="rm.safeguards[safeguard.id].list && rm.safeguards[safeguard.id].list.length > 0" class="pb-3">
                Safeguard to be applied to:
                <div v-for="item in rm.safeguards[safeguard.id].list" :key="item">
                  <i class="fa fa-caret-right pr-2"></i>
                  {{item}}
                </div>
              </div>
              <div v-if="listDiff(rm.safeguards[safeguard.id].list, safeguard.negList).length > 0" class="pb-3">
                At this time, safeguard will not be applied to:
                <div v-for="item in listDiff(rm.safeguards[safeguard.id].list, safeguard.negList)" :key="item">
                  <i class="fa fa-caret-right pr-2"></i>
                  {{item}}
                </div>
              </div>

              <div>
                Estimated (First Year) Cost: {{rm.safeguards[safeguard.id].cost|toCurrency(0)}}
                <br />
                Responsible Person: {{rm.safeguards[safeguard.id].assignee}}
                <br />
                Implementation Due Date: {{rm.safeguards[safeguard.id].dueDate|formatDate('YYYY-MM-DD')}}
              </div>
            </div>

            <div v-else>
              <div>
                The Practice has determined not to implement this safeguard for the following reason:
                <br />
                {{riskMap.SafeguardReason[rm.safeguards[safeguard.id].reason].title}}
                <br />
                {{rm.safeguards[safeguard.id].reasonDetails}}
              </div>
            </div>

            <hr />
          </div>

          <h4 class="pt-4">5. Approval</h4>
          <p>This Risk Management Plan Report certifies and documents a risk management plan was completed, reviewed and approved by the Practice’s Privacy Officer.</p>
          <p>
            Privacy Officer:
            <span class="ra-highlight">{{rm.seed.practice.po.name}}</span>
            <br />Approved on:
            <span class="ra-highlight">{{rm.completedDate|formatDate('YYYY-MM-DD HH:MM:SS')}}</span>
          </p>

          <div v-if="rm.tasks">
            <hr />
            <h3>Appendix: Associated mitigation Tasks</h3>
            <p>Detailed list of mitigation tasks associate with this risk management plan. All tasks are finalized at the time a subsequent risk management plan is approved. At finalization, any tasks marked Open are deferred indefinitely.</p>

            <div v-for="task in taskList" :key="task.id" class="pt-4">
              <h4>{{task.title}}</h4>
              <div class="font-weight-bold">
                Task Status: {{riskMap.TaskStatus[task.status]}}
                <span v-if="task.status == 'completed' || task.status == 'removed'">on {{task.completedDate|formatDate('YYYY-MM-DD')}}</span>
              </div>

              <div v-if="task.completedReason && task.completedReason.length > 0" class="font-weight-bold pt-3">Reason: {{task.completedReason}}</div>

              <div v-if="task.list && task.list.length > 0" class="pt-3">
                Safeguard to be applied to:
                <div v-for="item in task.list" :key="item">
                  <i class="fa fa-caret-right pr-2"></i>
                  {{item}}
                </div>
              </div>

              <div class="pt-3">
                Estimated (First Year) Cost: {{task.cost|toCurrency(0)}}
                <br />
                Responsible Person: {{task.assignee}}
                <br />
                Implementation Due Date: {{task.dueDate|formatDate('YYYY-MM-DD')}}
              </div>

              <div v-if="task.note" class="pt-3">
                Task Notes:
                <div>{{task.note}}</div>
              </div>
            </div>
          </div>

          <hr />
          <b-row v-if="!id">
            <b-col cols="6">
              <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/risk/plan">Back</b-button>
            </b-col>
            <b-col cols="6" class="text-right">
              <b-button type="button" variant="primary" class="px-4" :disabled="busy" @click="dlgTaskWarning = true;">Approve</b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>

    <!-- Task Warning Modal Dialog -->
    <b-modal id="dlgTaskWarning" class="modal-primary" hide-header-close v-model="dlgTaskWarning">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fa-warning pr-2"></i>Existing Mitigation Task Warning
      </div>
      <p>Existing mitigation tasks that are still &quot;Open&quot; will be deferred indefinitely. There is nothing wrong with deferring tasks because you are about to approve a new (more current) risk management plan.</p>
      <div class="pt-2">
        <p>Select &quot;No&quot; to go to Mitigation Tasks and manually remove open tasks.</p>
        <p>Select &quot;Yes&quot; if you want to defer existing open tasks and finalize this report.</p>
      </div>
      <div slot="modal-footer">
        <b-button variant="default" @click="dlgTaskWarning = false">No</b-button>
        <b-button variant="primary" class="ml-2" @click="dlgTaskWarning = false; onSubmit();">Yes</b-button>
      </div>
    </b-modal>

    <!-- Approved Modal Dialog -->
    <b-modal id="dlgApproved" class="modal-primary" hide-header-close v-model="dlgApproved">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fa-rocket pr-2"></i>Risk Management Plan Complete
      </div>
      <p>Congratulations! You've completed a risk management plan. Now it's time to work on any mitigation tasks created by the plan.</p>
      <div slot="modal-footer">
        <b-button variant="primary" class="ml-2" @click="onClose">Continue</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { riskService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'RiskPlanReport',
  props: ['id'],
  data() {
    return {
      rm: {
        seed: {
          practice: {
            po: {}
          },
          workforce: {}
        },
        threats: [],
        safeguards: {}
      },
      safeguards: [],
      dlgTaskWarning: false,
      dlgApproved: false,
      ready: false,
      busy: false,
      submitted: false,
      currentStep: 6
    }
  },
  computed: {
    ...mapState(['rmSteps']),
    ...mapState('maps', ['inventoryMap', 'riskMap', 'safeguardsMap', 'threatMap']),
    safeguardsNeg() {
      return Enumerable.from(this.safeguards)
        .where(x => { return x.negEval && (x.type == 'required' || x.type == 'addressable'); })
        .toArray();
    },
    taskList() {
      return Enumerable.from(this.rm.tasks)
        .join(JSON.parse(JSON.stringify(this.safeguardsMap)),
          function (o) { return o.key; },
          function (i) { return i.id; },
          function (o, i) { return { id: i.id, title: i.title, status: o.value.status, list: o.value.list, cost: o.value.cost, assignee: o.value.assignee, note: o.value.note, dueDate: o.value.dueDate, completedDate: o.value.completedDate, completedReason: o.value.completedReason }; })
        .orderBy(x => { return x.id; })
        .toArray();
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertException: 'alert/exception',
      setProgress: 'progress/setFlagDone'
    }),
    listDiff(list1, list2) {
      return Enumerable.from(list2).except(list1).toArray();
    },
    threatsForSafeguard(id) {
      return Enumerable.from(this.threatMap)
        .join(this.rm.threats,
          (o) => { return o.id + ':' + o.revision; },
          (i) => { return i.id + ':' + i.revision; },
          (o, i) => { return { id: o.id, threat: o.threat, threatInfo: o.threatInfo, vuln: o.vuln, vulnInfo: o.vulnInfo, active: i.active, plan: i.plan, safeguards: o.safeguards, risk: i.chance * i.impact }; })
        .where(x => { return x.active && x.safeguards.indexOf(id) > -1; })
        .toArray();
    },
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.rm.completed = true;
      this.rm.completedDate = new Date();
      this.rm.step = this.currentStep;

      riskService.setRiskManagement(this.rm)
        .then(data => {
          this.setProgress('RMSet');
          this.$confetti.start({ canvasId: null, particles: [{ type: 'rect' }] });

          // show approved dialog
          this.dlgApproved = true;
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving risk management.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onClose() {
      this.$confetti.stop();
      this.dlgApproved = false;
      this.$router.push('/task-list');
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchInventoryMap'),
        this.$store.dispatch('maps/fetchRiskMap'),
        this.$store.dispatch('maps/fetchSafeguardsMap'),
        this.$store.dispatch('maps/fetchThreatMap'),
        this.id && this.id.length > 0 ? riskService.getRiskManagement(this.id) : riskService.getCurrentRiskManagement()
      ]).then((values) => {
        let rm = values[4];
        if (!rm.completed) {
          rm.completedDate = new Date();
        }

        this.safeguards = riskService.updateSafeguards(rm, this.threatMap, this.safeguardsMap, this.inventoryMap);
        this.rm = rm;
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading risk management data.' });
      }).finally(() => {
        this.ready = true;
      })
    }
  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>

<style lang="scss">
// import variables for this application
@import '@/assets/scss/_variables.scss';

.ra-highlight {
  color: $highlight-color;
  background-color: $highlight-bg;
  display: inline;
}
.ra-highlight-pre {
  color: $highlight-color;
  background-color: $highlight-bg;
  white-space: pre-wrap;
}
.prewrap {
  white-space: pre-wrap;
}
</style>